import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Sparkles, ExternalLink, Calendar, MapPin } from "lucide-react";

/**
 * Vente flash Chako Design — 3 jours (Ven 11 · Sam 12 · Dim 13 septembre 2026)
 * Accord L'Épicurienne Trail obtenu · vente directe via QR codes → arteurs.com
 *
 * S'affiche uniquement dans l'article /carnet/epicurienne-x-chako-design.
 * Ne s'affiche plus une fois l'événement terminé (14 sept 2026).
 */

// Date de lancement : Ven 11 sept 2026 · 09h00 (heure de Paris)
const START_DATE = new Date("2026-09-11T09:00:00+02:00");
// Fin de la vente : Dim 13 sept 2026 · 23h59
const END_DATE = new Date("2026-09-13T23:59:59+02:00");

const CHAKO_SHOP_URL = "https://arteurs.com/product-category/mobilier/";

function useCountdown(target) {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);
  const diff = Math.max(0, target - now);
  const d = Math.floor(diff / (1000 * 60 * 60 * 24));
  const h = Math.floor((diff / (1000 * 60 * 60)) % 24);
  const m = Math.floor((diff / (1000 * 60)) % 60);
  const s = Math.floor((diff / 1000) % 60);
  return { d, h, m, s, diff };
}

export default function VenteFlashChako() {
  const now = new Date();
  const isOver = now > END_DATE;
  const isLive = now >= START_DATE && now <= END_DATE;
  const target = isLive ? END_DATE : START_DATE;
  const { d, h, m, s } = useCountdown(target);

  if (isOver) return null; // Ne s'affiche pas après la vente

  return (
    <motion.section
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8 }}
      className="mt-20 md:mt-24 max-w-[720px] border border-cinabre/40 bg-cinabre/[0.03] p-8 md:p-12 relative overflow-hidden"
      data-testid="vente-flash-chako"
    >
      {/* Decorative corner */}
      <div className="absolute top-0 right-0 w-24 h-24 bg-cinabre/10 blur-3xl rounded-full" aria-hidden />

      <div className="relative">
        <div className="flex items-center gap-3">
          <Sparkles size={16} className="text-cinabre" strokeWidth={1.5} />
          <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-cinabre">
            § Vente flash · 3 jours · {isLive ? "En cours" : "Bientôt"}
          </div>
        </div>

        <h2 className="mt-5 font-serif font-light text-3xl md:text-5xl leading-[0.98] tracking-tight text-fg">
          Mobilier & luminaires <em className="italic">Chako Design</em>.
        </h2>

        <p className="mt-5 text-fg/80 leading-relaxed">
          Trois jours pour rapporter chez vous une pièce vue au village de départ. <strong>Vendredi 11 · Samedi 12 · Dimanche 13 septembre 2026</strong> — sélection Chako en vente directe, mobilier tressé et luminaires. Chaque acquisition prolonge le geste du fil : art, artisanat, terroir, canal.
        </p>

        {/* Countdown */}
        <div className="mt-8 border-y border-cinabre/30 py-6 grid grid-cols-4 gap-3 md:gap-6" data-testid="vente-flash-countdown">
          {[
            { label: "jours", value: d },
            { label: "heures", value: h },
            { label: "minutes", value: m },
            { label: "secondes", value: s },
          ].map((unit) => (
            <div key={unit.label} className="text-center">
              <div className="font-serif text-3xl md:text-5xl leading-none tracking-tight text-cinabre tabular-nums">
                {String(unit.value).padStart(2, "0")}
              </div>
              <div className="mt-2 font-mono text-[9px] md:text-[10px] tracking-[0.24em] uppercase text-fg/60">
                {unit.label}
              </div>
            </div>
          ))}
        </div>
        <div className="mt-3 font-mono text-[10px] tracking-[0.24em] uppercase text-fg/50 text-center">
          {isLive ? "→ Clôture dimanche 13 sept · 23h59" : "→ Ouverture vendredi 11 sept · 09h00"}
        </div>

        {/* Meta */}
        <div className="mt-8 grid md:grid-cols-2 gap-4 text-[14px]">
          <div className="flex items-start gap-3">
            <Calendar size={16} className="text-cinabre mt-0.5 shrink-0" strokeWidth={1.5} />
            <div>
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">§ Dates</div>
              <div className="mt-1 font-serif text-fg/90 leading-snug">Ven 11 · Sam 12 · Dim 13 septembre 2026</div>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <MapPin size={16} className="text-cinabre mt-0.5 shrink-0" strokeWidth={1.5} />
            <div>
              <div className="font-mono text-[10px] tracking-[0.24em] uppercase text-fg/60">§ Sur place</div>
              <div className="mt-1 font-serif text-fg/90 leading-snug">Village de départ · L'Épicurienne Trail · Castelnau-d'Estrétefonds</div>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-10 flex flex-wrap items-center gap-4">
          <a
            href={CHAKO_SHOP_URL}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-3 bg-cinabre text-bg px-6 py-4 font-mono text-[11px] tracking-[0.24em] uppercase hover:bg-fg transition-colors group"
            data-testid="vente-flash-cta-shop"
          >
            Voir la sélection Chako
            <ExternalLink size={14} className="group-hover:translate-x-0.5 transition-transform" />
          </a>
          <a
            href="mailto:arteurs.lecollectif@gmail.com?subject=Vente%20flash%20Chako%20-%2011-13%20septembre"
            className="inline-flex items-center gap-2 font-mono text-[11px] tracking-[0.24em] uppercase text-fg/70 hover:text-cinabre transition-colors border-b border-fg/20 hover:border-cinabre pb-1"
            data-testid="vente-flash-cta-reserve"
          >
            Réserver / poser une question →
          </a>
        </div>

        <p className="mt-8 text-[12px] text-fg/50 italic leading-relaxed">
          Vente organisée avec l'accord de <a href="https://www.epicurienne-trail.com/edition-2025" target="_blank" rel="noreferrer" className="underline decoration-cinabre decoration-1 underline-offset-4 hover:text-cinabre transition-colors">L'Épicurienne Trail</a>. Paiement direct via QR codes ou sur place. Livraison ou remise en main propre à Castelnau d'Estrétefonds.
        </p>
      </div>
    </motion.section>
  );
}
